/**
 * A recipe card in the home feed. The whole card is a link to the recipe view
 * (`/recipes/:id`, built in Steg 1b-ii).
 *
 * The thumbnail shows the recipe's hero image when set (Steg 3b), else an
 * honest striped placeholder rather than a fake photo. A `featured` card spans
 * the row and shows the intro paragraph + tags.
 *
 * Ukeplanlegger drag-and-drop (Slice 4, whole-card in 4b): when
 * `planDraggable` is true (a writer) AND the viewport is at/above the
 * planner's desktop breakpoint (`usePlannerDesktop`), grabbing ANYWHERE on
 * the card starts a drag — `{...listeners} {...attributes}` sit on the card
 * itself (`setNodeRef`), not on a small handle. This replaces the original
 * ⠿-grip-only design (Slice 4): prod feedback said the grip wasn't
 * discoverable, so the whole card is now the drag affordance
 * (`cursor: grab`/`grabbing`, see `app.css`). `activationConstraint: {
 * distance: 4 }` (set on the shared `PointerSensor` in `HomePage`) is what
 * keeps a plain click/tap still reading as a click, not an instant drag.
 *
 * `disabled: !planDraggable || !isPlannerDesktop` is the actual guard that
 * keeps this desktop-only — see `usePlannerDesktop`'s docs for why that has
 * to be a real (reactive) JS check now, not just the old CSS
 * `display: none` on the grip: once the card itself carries pointer
 * listeners, a mobile scroll gesture starting on a card would otherwise be
 * captured as a drag.
 *
 * Keyboard accessibility: a `<div>`/`<a>` card is not a natural drag
 * activator for dnd-kit's `KeyboardSensor` in the same way a focusable
 * `<button>` is, so the ⠿ grip STAYS as the dedicated keyboard activator
 * (`setActivatorNodeRef` + its own `{...attributes} {...listeners}`) — it is
 * always rendered when `planDraggable` is true (not gated to desktop, so Tab
 * order doesn't change across a resize) but visually hidden until it
 * receives keyboard focus (`.recipe-card-drag-handle:focus-visible` in
 * `app.css`), so mouse users never see two drag affordances. The grip's own
 * click is separately swallowed (`preventDefault`/`stopPropagation`, same
 * idiom as the quick-add button) so a plain tap on the grip itself never
 * navigates either.
 */

import type { RecipeSummary } from "@tjommi/shared";
import { useDraggable } from "@dnd-kit/core";
import React from "react";
import { Link } from "react-router-dom";

import { formatTotalTime } from "../lib/formatTime";
import { recipeDragId, type MealPlanDropRecipe } from "../lib/mealPlanDrag";
import { usePlannerDesktop } from "../lib/usePlannerDesktop";

function metaParts(recipe: RecipeSummary): string[] {
  const parts: string[] = [];
  if (recipe.totalTimeMinutes != null) parts.push(`⏱ ${formatTotalTime(recipe.totalTimeMinutes)}`);
  if (recipe.servings != null) parts.push(`🍽 ${recipe.servings} porsjoner`);
  return parts;
}

export function RecipeCard({
  recipe,
  featured = false,
  cookbookLabel,
  onNavigate,
  onQuickAdd,
  planDraggable = false,
  onApproveReview,
}: {
  recipe: RecipeSummary;
  featured?: boolean;
  /**
   * Which cookbook this result belongs to — shown only when provided (Steg 5
   * cross-cookbook search results). The feed's own single-cookbook usage is
   * unchanged (prop omitted → nothing rendered).
   */
  cookbookLabel?: string;
  /**
   * Optional interception hook, called before navigating (e.g. to switch the
   * active cookbook context for a cross-cookbook search result). Return value
   * is ignored; navigation to `/recipes/:id` still happens via the Link.
   */
  onNavigate?: () => void;
  /**
   * "+" quick-action (Ukeplanlegger, Slice 3) — opens the add-to-calendar
   * bottom sheet for THIS recipe instead of navigating. Mobile-only affordance
   * (desktop uses the recipe detail page's dialog and the drag handle below);
   * omitted entirely ⇒ no button renders (e.g. a read/comment member, or a
   * caller that doesn't wire the planner — same opt-in idiom as `onNavigate`).
   */
  onQuickAdd?: (recipe: RecipeSummary) => void;
  /**
   * Ukeplanlegger drag-and-drop (Slice 4, whole-card in 4b) — registers the
   * card as a dnd-kit draggable (id `recipeDragId(recipe.id)`,
   * `data: { recipe }`) so `HomePage`'s shared `DndContext` can resolve a drop
   * onto a planner day, and renders the keyboard-only ⠿ grip. The caller
   * (HomePage) gates this to a writer; hidden entirely for a read/comment
   * member (no listeners, no grip ⇒ no drag ⇒ no write call can ever fire).
   * Desktop-only via `usePlannerDesktop` (see the file docs above) — mobile
   * keeps the "+" quick-add sheet instead.
   */
  planDraggable?: boolean;
  /**
   * One-click "approve from the feed" (pdf-import review flow) — renders a
   * small × on the "Til gjennomgang" badge when provided (the caller, e.g.
   * `HomePage`, only wires this for a writer; a read/comment member sees the
   * badge but no ×, same opt-in idiom as `onQuickAdd`/`planDraggable`). The ×
   * only ever renders when BOTH this is set AND `recipe.reviewState ===
   * "needs_review"` — the caller passes an already-overridden `recipe` (with
   * `reviewState` cleared) once the approve call is optimistically in
   * flight/succeeded, so this button — and the badge itself — disappear
   * together without any extra prop needed here.
   */
  onApproveReview?: (recipe: RecipeSummary) => void;
}): React.JSX.Element {
  const parts = metaParts(recipe);
  const isPlannerDesktop = usePlannerDesktop();
  const dropRecipe: MealPlanDropRecipe = {
    id: recipe.id,
    title: recipe.title,
    servings: recipe.servings,
    heroImageUrl: recipe.heroImageUrl,
  };
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: recipeDragId(recipe.id),
    data: { recipe: dropRecipe },
    disabled: !planDraggable || !isPlannerDesktop,
  });

  return (
    <Link
      ref={setNodeRef}
      to={`/recipes/${recipe.id}`}
      className={`recipe-card${featured ? " featured" : ""}${isDragging ? " is-dragging" : ""}${planDraggable && isPlannerDesktop ? " is-draggable" : ""}`}
      data-testid="recipe-card"
      data-recipe-id={recipe.id}
      onClick={onNavigate}
      {...(planDraggable && isPlannerDesktop ? { ...attributes, ...listeners } : {})}
    >
      <div className="recipe-thumb">
        {recipe.heroImageUrl && (
          <img
            src={recipe.heroImageUrl}
            alt=""
            className="recipe-thumb-img"
            data-testid="recipe-thumb-image"
          />
        )}
        {recipe.category && <span className="cat-tag">{recipe.category}</span>}
        {recipe.reviewState === "needs_review" && (
          <span className="review-tag" data-testid="recipe-review-badge">
            Til gjennomgang
            {onApproveReview && (
              <button
                type="button"
                className="review-tag-approve"
                aria-label={`Merk "${recipe.title}" som gjennomgått`}
                data-testid="recipe-review-approve"
                onClick={(e) => {
                  // Same click-vs-drag/click-vs-nav idiom as the "+" quick-add
                  // and the ⠿ drag handle: swallow the click entirely so it
                  // never bubbles to the enclosing `Link` (no navigation) and
                  // never reads as the start of a whole-card planner drag —
                  // dnd-kit's PointerSensor only ever promotes a pointerdown
                  // to a drag past `activationConstraint.distance` (4px), so a
                  // stationary click here never crosses that threshold
                  // regardless of propagation.
                  e.preventDefault();
                  e.stopPropagation();
                  onApproveReview(recipe);
                }}
              >
                ×
              </button>
            )}
          </span>
        )}
        {planDraggable && (
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="recipe-card-drag-handle"
            aria-label={`Dra ${recipe.title} for å planlegge i ukeplanen`}
            data-testid="recipe-card-drag-handle"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        )}
        {onQuickAdd && (
          <button
            type="button"
            className="recipe-card-quick-add"
            data-testid="recipe-card-quick-add"
            aria-label={`Legg ${recipe.title} til i ukeplanen`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onQuickAdd(recipe);
            }}
          >
            +
          </button>
        )}
      </div>
      <div className="recipe-body">
        {cookbookLabel && (
          <span className="recipe-cookbook-label" data-testid="recipe-cookbook-label">
            📖 {cookbookLabel}
          </span>
        )}
        <div className="recipe-title">{recipe.title}</div>

        {featured && recipe.intro && <p className="recipe-intro">{recipe.intro}</p>}

        {parts.length > 0 && (
          <div className="recipe-meta">
            {parts.map((p) => (
              <span key={p}>{p}</span>
            ))}
          </div>
        )}

        {featured && recipe.tags.length > 0 && (
          <div className="recipe-meta" style={{ gap: "6px" }}>
            {recipe.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="chip">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
